import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import { IShift, IShiftService, ShiftService, ShiftStatus, TillType, OpenHours, IPaymentMethod } from '@posx/core';
import { usePrinterStore } from './printer';
import { Subscription } from 'dexie';
import { usePaymentStore } from './payment';
import { useProductStore } from './product';
import { preciseRound } from '@/utils';
import { getPlatform } from '@/utils/misc';
import { useDevice } from './device.store';
import { useConfigStore } from './config';
import { useEmployeeStore } from './employee';
import { logLiveQueryError, logError, logInfo, isLiveQueryCursorError } from '@/utils/logger';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { WorkManager } from '@/workers/work.manager';

export const useShiftStore = defineStore('shift', {
  state: () => ({
    currentShift: null as IShift | null,
    service: null as IShiftService | null,
    _subscription: null as Subscription | null,
  }),
  getters: {
    // Expose the service instance for direct access from components
    shiftService: (state) => state.service,
    db: (state) => state.service?.db,
  },
  actions: {
    async getService(): Promise<IShiftService> {
      if (this.service) return this.service;
      // service singleton is method-only, skip deep proxy
      this.service = markRaw(await ServicesBuilder.getInstance().getService<IShiftService>(ShiftService));
      return this.service;
    },

    async startShiftLiveQuery() {
      if (getPlatform() === 'mobile-web') return;
      if (this._subscription) return;
      const service = await this.getService();
      if (this._subscription) return; // re-check after await to close TOCTOU race window
      const subscription = service.liveQuery(() => service.getOneByParams({ status: ShiftStatus.Open }))
        .subscribe({ next: (result: IShift | undefined) => { this.currentShift = result ?? null; },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'shift' }); if (isLiveQueryCursorError(err) && this._subscription === subscription) { subscription.unsubscribe(); this._subscription = null; } }
        });
      this._subscription = markRaw(subscription);
      WorkManager.getInstance().startAutoShiftWorker(() => this.checkAndAutoOpenShift(), 60000);
    },
    // Stop live query subscription to prevent memory leak
    stopLiveQuery() {
      this._subscription?.unsubscribe();
      this._subscription = null;
    },

    async cashIn(amount: number, note: string, paymentMethod?: IPaymentMethod) {
      const service = await this.getService();
      const result = await service.cashIn(amount, note, paymentMethod);
      // core stamps the till with the shift opener, so log the acting staff
      logInfo(`Shift: Cash In, Shift ${this.currentShift?.uid}`, { staff_uid: useEmployeeStore().currentStaff?.uid, amount, reason: note });
      return result;
    },
    async cashOut(amount: number, note: string, paymentMethod?: IPaymentMethod) {
      const service = await this.getService();
      const result = await service.cashOut(amount, note, paymentMethod);
      logInfo(`Shift: Cash Out, Shift ${this.currentShift?.uid}`, { staff_uid: useEmployeeStore().currentStaff?.uid, amount, reason: note });
      return result;
    },
    async getShiftStatus() {
      if (getPlatform() === 'mobile-web') return;
      const service = await this.getService();
      const result = await service.getOneByParams({
        status: ShiftStatus.Open,
      });
      this.currentShift = result ?? null;
      return result;
    },
    async openShift(openAmount: number, userUid: string, note?: string) {
      const service = await this.getService();
      const printerStore = usePrinterStore();
      const printerService = await printerStore.getService();
      const deviceStore = useDevice();
      await deviceStore.openCashDrawer();
      const result = await service.openShift(openAmount, userUid, note);
      this.currentShift = result;
      logInfo(`Shift: Opened, Shift ${result.uid}`, { staff_uid: userUid, opening_balance: openAmount });
      void useProductStore().resetAutoSoldOutItems(userUid); // remote write, don't block the shift dialog
      return result;
    },
    async closeShift(amount: number, userUid: string) {
      const service = await this.getService();
      const printerStore = usePrinterStore();
      const printerService = await printerStore.getService();
      const deviceStore = useDevice();
      await deviceStore.openCashDrawer();
      const shift = this.currentShift; // close returns the PATCH response; keep uid + expected drawer_amount
      const result = await service.closeShift(amount, userUid);
      logInfo(`Shift: Closed, Shift ${shift?.uid}`, { staff_uid: userUid, closing_balance: amount, expected: shift?.drawer_amount });
      this.currentShift = null;
      return result;
    },
    async calculateAndUpdateDrawerAmount() {
      if (!this.currentShift?.uid) return 0;

      const paymentStore = usePaymentStore();
      const tillService = await paymentStore.getTillService();
      const tills = await tillService.getAll();

      // Calculate and update drawer amount
      return this.currentShift.drawer_amount = preciseRound(tills
        .filter(till => till.shift_uid === this.currentShift?.uid
          && (till.deleted_at === null || till.deleted_at === undefined))
        .reduce((acc, till) => acc + (till.type === TillType.CashOut ? -till.amount : till.amount), 0));
    },
    // Auto-open a shift once per day when opening hours begin (main device only); driven by WorkManager
    async checkAndAutoOpenShift() {
      if (secureStorage.getCachedItem('is_main_device') !== 'true') return;
      const tillConfig = useConfigStore().appConfig?.config?.till;
      if (!tillConfig?.auto_start_shift || this.currentShift) return;
      if (!this.isWithinOpeningHours() || await this.hasShiftToday()) return;
      try { await this.autoOpenShift(tillConfig.auto_shift_amount ?? 0); }
      catch (e) { logError('Auto open shift failed', { error: e }); }
    },
    // A shift was already created today (open or closed) -> don't auto-open again.
    // Range-scan the created_at_timestamp index instead of loading the whole table.
    async hasShiftToday(): Promise<boolean> {
      const start = new Date(); start.setHours(0, 0, 0, 0);
      const service = await this.getService();
      return (await service.table.where('created_at_timestamp').aboveOrEqual(start.getTime())
        .filter((s: IShift) => !s.deleted_at).count()) > 0;
    },
    // Open shift without cash drawer (auto-shift), only records till
    async autoOpenShift(amount: number) {
      const service = await this.getService();
      this.currentShift = await service.openShift(amount, 'system');
      void useProductStore().resetAutoSoldOutItems('system'); // remote write, don't block auto shift
      return this.currentShift;
    },
    isWithinOpeningHours(): boolean {
      const now = new Date();
      const cur = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      return this.getOpeningHoursForDate(now).some((h) => !!h?.open && !!h?.close
        && (h.close <= h.open ? cur >= h.open || cur < h.close : cur >= h.open && cur < h.close)); // overnight-aware
    },
    // Effective opening hour ranges for a date; priority: special_days > holidays(closed) > weekly_schedules > default
    getOpeningHoursForDate(date: Date = new Date()): OpenHours[] {
      const schedules = useConfigStore().appConfig?.config?.general?.opening_schedules;
      if (!schedules?.length) return [];
      const dateStr = date.toLocaleDateString('en-CA'); // local YYYY-MM-DD
      const day = (['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const)[date.getDay()];
      return schedules.flatMap((s) => {
        const special = s.special_days?.find((d) => d.date === dateStr);
        if (special) return special.closed ? [] : special.hours;
        if (s.holidays?.some((h) => h.date === dateStr)) return [];
        const weekly = s.weekly_schedules?.find((w) => w.days?.includes(day));
        if (weekly) return weekly.closed ? [] : (weekly.hours ?? []);
        return s.opening_hours ? [s.opening_hours] : [];
      });
    },
  },
});
